/* tslint:disable */
import { Injectable } from '@angular/core';
import { RectangularROI, EllipsoidROI } from "./ShapeROIsModel";
import { WebGLService } from "../views/three-dview/web-gl.service";
import { TwoDService } from "../views/two-dview/two-d.service";
import * as THREE from 'three';
import { MeshService } from "../views/three-dview/mesh.service";
import { StatusMessageService } from "../status-message.service";
import { TabService } from "../../tab.service";
import { UserService } from '../user-management/user.service';

@Injectable({
    providedIn: 'root'
})
export class ShapeROIsService {

    rois = [];

    activeROIIndex: number;
    activeROI: EllipsoidROI;
    defaultROIsOpacity = 0.2;
    defaultROIRadius = 100; // 100 pixel
    isROIsDrawingMode: boolean;
    activeROI2DData: {
        startPos: THREE.Vector2,
        endPos: THREE.Vector2,
        radius: number
    } = {
            startPos: null,
            endPos: null,
            radius: 100
        };


    rectangularRois = []
    activeRectangularROI: RectangularROI = null;
    isRectangularROIsDrawingMode: boolean;
    activeRectangularROIData: {
        startPos: THREE.Vector2,
        endPos: THREE.Vector2,
        setStartPos,
        setEndPos,
    } = {
            startPos: new THREE.Vector2(0, 0),
            endPos: new THREE.Vector2(0, 0),
            setStartPos(x, y) {
                this.startPos.x = x;
                this.startPos.y = y;
            },
            setEndPos(x, y) {
                this.endPos.x = x;
                this.endPos.y = y;
            },

        };



    constructor(private webGLService: WebGLService,
        private twoDService: TwoDService,
        private tabService: TabService,
        private statusMessageService: StatusMessageService,
        private userService: UserService) {

        this.isROIsDrawingMode = false;
        this.activeROI = null;
    }


    getROIData(roiId) {
        for (let i = 0; i < this.rois.length; i++) {
            if (this.rois[i].id === roiId) {
                return this.rois[i].serialize();
            }
        }
        return null;
    }

    getROIById(roiId) {
        for (let i = 0; i < this.rois.length; i++) {
            if (this.rois[i].id === roiId) {
                return this.rois[i];
            }
        }
        return null;
    }

    setROIName(roiId, roiNewName) {
        let roi = this.getROIById(roiId);
        if (roi) {
            roi.name = roiNewName;
            return true;
        }
        return false;
    }


    updateRoiFromJSON(roi, data, positionIn3DScene, scale, meshPosition) {
        roi.copyData(data);
        positionIn3DScene = new THREE.Vector3(data.mesh.position.x, data.mesh.position.y, data.mesh.position.z);
        roi.positions._3d = positionIn3DScene;
        // scale.set(JSONData.mesh.a, JSONData.mesh.b, JSONData.mesh.c);
        scale = new THREE.Vector3(data.mesh.a, data.mesh.b, data.mesh.c);
        meshPosition = positionIn3DScene;
        this.updateROIRadius(roi);
        return { 'scale': scale, 'meshPosition': meshPosition };
    }

    createNewROI(positionIn3DScene: THREE.Vector3 = null, JSONData = null, hasJson = false, mirrorROI = false, shareViewLinkROIData = null, dbId = null) {
        if (positionIn3DScene === null) {
            // The brain in the dorsal images(tif file) is slightly off-center, which is why we manually determined the X-axis center.
            positionIn3DScene = new THREE.Vector3(284, 974 / 2, 358 / 2);
        }

        let newROI = new EllipsoidROI(hasJson, dbId);
        let scale = new THREE.Vector3(this.defaultROIRadius, this.defaultROIRadius, this.defaultROIRadius);

        let meshPosition;

        if (JSONData) {
            let returnedData = this.updateRoiFromJSON(newROI, JSONData, positionIn3DScene, scale, meshPosition);
            scale = returnedData['scale'];
            meshPosition = returnedData['meshPosition'];
        } else if (JSONData == null && shareViewLinkROIData == null) {
            // positionIn3DScene: is the real center position in tif file
            newROI.positions._3d = positionIn3DScene;
            if (!mirrorROI) {
                meshPosition = this.convertFromRealPositionToBrainSpace(newROI.positions._3d);
            } else {
                meshPosition = new THREE.Vector3(
                    newROI.positions._3d.x,
                    newROI.positions._3d.y,
                    newROI.positions._3d.z
                );
            }
        } else if (shareViewLinkROIData) {
            let returnedData = this.updateRoiFromJSON(newROI, shareViewLinkROIData, positionIn3DScene, scale, meshPosition);
            scale = returnedData['scale'];
            meshPosition = returnedData['meshPosition'];
        }

        this.rois.push(newROI);
        this.activeROIIndex = this.rois.length - 1;
        this.activateROIByIndex(this.activeROIIndex);
        this.activeROI = newROI;
        this.addROITo3DScene(newROI, meshPosition, scale);

        if (scale) {
            newROI.mesh.scale.set(scale.x, scale.y, scale.z);
            const isDesiredData = shareViewLinkROIData || JSONData;
            const activeROIScale = isDesiredData ? newROI.mesh.scale : this.defaultROIRadius;
            this.updateActiveROI(newROI.mesh.position, activeROIScale, isDesiredData);
        }

        this.addROITo2DScene(newROI);
        return newROI;

    };

    activateROIByIndex(activeROIIndex) {
        for (let i = 0; i < this.rois.length; i++) {
            if (i === activeROIIndex) {
                this.rois[i].isActive = true;
                this.activeROIIndex = i;
                this.activeROI = this.rois[i];
            } else {
                this.rois[i].isActive = false;
            }
        }
    };

    activateROI(roi) {
        roi.isActive = true;
        roi.selected = true;
        this.activeROI = roi;
        for (let i = 0; i < this.rois.length; i++) {
            let currentROI = this.rois[i];
            if (currentROI !== roi) {
                currentROI.isActive = false;
            } else {
                this.activeROIIndex = i;
            }
        }
    };

    activateROIByMesh(mesh) {
        for (let i = 0; i < this.rois.length; i++) {
            let currentROI = this.rois[i];
            if (currentROI.mesh === mesh) {
                this.activateROI(currentROI);
                break;
            }
        }
    };

    updateROIRadius(roi) {
        if (roi) {
            this.updateROIBackendData(roi);
            this.twoDService.updateROI2DData();
        }
    };

    updateActiveROIRadiusFrom2D() {
        if (this.activeROI) {
            this.activeROI.radii._2d.dorsal.x = this.activeROI2DData.radius;
            this.activeROI.radii._2d.dorsal.y = this.activeROI2DData.radius;

            this.activeROI.radii._2d.sagittal.x = this.activeROI2DData.radius;
            this.activeROI.radii._2d.sagittal.y = this.activeROI2DData.radius;

            this.activeROI.radii._2d.coronal.x = this.activeROI2DData.radius;
            this.activeROI.radii._2d.coronal.y = this.activeROI2DData.radius;

            this.activeROI.radii._3d = this.activeROI2DData.radius;
            this.activeROI.mesh.scale.x = this.activeROI2DData.radius;
            this.activeROI.mesh.scale.y = this.activeROI2DData.radius;
            this.activeROI.mesh.scale.z = this.activeROI2DData.radius;

            this.updateROIBackendData(this.activeROI);
            this.twoDService.updateROI2DData();
        }
    };

    //
    // updateActiveRectangularROIStartingPointFrom2D() {
    //     let topLeftCornerPoint = {
    //         x: Math.min(this.activeRectangularROIData.startPos.x, this.activeRectangularROIData.endPos.x),
    //         y: Math.min(this.activeRectangularROIData.startPos.y, this.activeRectangularROIData.endPos.y)
    //     }
    //
    //     // Set the ROI's ORIGINAL initial position in the tif file in the top-left corner.
    //     this.activeRectangularROI.roiMesh["start"] = new THREE.Vector3(
    //         topLeftCornerPoint.x,
    //         topLeftCornerPoint.y,
    //         SelectedPosition.coordinates.z
    //     );
    //     this.twoDService.updateScene();
    // };


    updateROIBackendData(roi) {
        if (roi.mesh) {
            let ROIRealPosition = this.convertFromBrainSpaceToRealPosition(roi.mesh.position);
            roi.backendData = {
                position: ROIRealPosition,

                rotation: new THREE.Vector3(
                    -roi.mesh.rotation.x,
                    -roi.mesh.rotation.y,
                    roi.mesh.rotation.z),

                a: roi.mesh.scale.x,
                b: roi.mesh.scale.y,
                c: roi.mesh.scale.z
            };
        }
    };

    convertFromBrainSpaceToRealPosition(itemPositionInBrainSpace) {
        return new THREE.Vector3(
            parseInt(itemPositionInBrainSpace.x + this.webGLService.referencePosition.x),
            parseInt(itemPositionInBrainSpace.y + this.webGLService.referencePosition.y),
            parseInt(itemPositionInBrainSpace.z + this.webGLService.referencePosition.z)
        )
    };

    convertFromRealPositionToBrainSpace(realPosition) {
        // realPosition: is the real center position in tif file
        // The referencePosition refers to the calculated center of the 3D brain model, obtained by applying the getMeshCenter function to the stl file.
        return new THREE.Vector3(
            realPosition.x - this.webGLService.referencePosition.x,
            realPosition.y - this.webGLService.referencePosition.y,
            realPosition.z - this.webGLService.referencePosition.z
        );
    };

    updateActiveROI(positionInWebGL, radius, isDesiredUserROIData = null) {
        this.activeROI.mesh.position.copy(positionInWebGL);
        const scale = isDesiredUserROIData ? { x: radius.x, y: radius.y, z: radius.z } : {
            x: radius,
            y: radius,
            z: radius
        };
        this.activeROI.mesh.scale.set(scale.x, scale.y, scale.z);

        this.updateROIBackendData(this.activeROI);
        this.twoDService.updateScene();
    }

    updateActiveROIFrom2D(positionIn3D) {
        this.activeROI.mesh.position.copy(this.convertFromRealPositionToBrainSpace(positionIn3D));
        this.updateROIBackendData(this.activeROI);
        this.twoDService.updateScene();
    }

    removeROI(roi) {
        this.removeROIFrom3DScene(roi);
        this.removeROIFrom2DScene(roi);
        let roiIndex = this.rois.indexOf(roi);
        if (roi.isActive) {
            if (roiIndex > 0) {
                this.activateROIByIndex(roiIndex - 1);
            } else if (roiIndex === 0) {
                if (this.rois.length > 1) {
                    this.activateROIByIndex(roiIndex + 1);
                } else {
                    this.activeROI = null;
                    this.activeROIIndex = -1;
                }
            }
        }
        this.rois.splice(roiIndex, 1);
    }


    //region 3D Drawing

    addROITo3DScene(roi: EllipsoidROI, position: THREE.Vector3, userScale = null) {
        if (roi.mesh === undefined) {
            let scale = new THREE.Vector3(1, 1, 1);
            if (userScale) {
                scale = new THREE.Vector3(userScale.x, userScale.y, userScale.z);
            }
            roi.mesh = MeshService.instance.createROIMesh(position, scale, roi.color, this.defaultROIsOpacity, []);
        }

        this.webGLService.addMeshToScene(roi.mesh);
        this.webGLService.mouse.actionModes.selectedRoi.meshes.push(roi.mesh);
    };

    removeROIFrom3DScene(roi) {
        this.webGLService.webGLVariables.scene.remove(roi.mesh);
        this.webGLService.mouse.actionModes.selectedRoi.meshes.splice(this.webGLService.mouse.actionModes.selectedRoi.meshes.indexOf(roi.mesh), 1);
    };

    //endregion

    // 2D Drawing

    addROITo2DScene(roi) {
        this.twoDService.addROITo2DScene(roi, true);
    };

    addRectangularROITo2DScene(roi) {
        this.twoDService.addRectangularROITo2DScene(roi, true);
    };

    removeROIFrom2DScene(roi) {
        this.twoDService.removeROIFrom2DScene(roi, true);
    };

    updateROIsIn2DScene() {
        // this.twoDService.updateActiveLayout();
    }


    getSelectedROIsShareView() {
        let data = [];
        for (let i = 0; i < this.rois.length; i++) {
            data[i] = this.rois[i].serialize()
        }
        return data;
    }

    //endregion

    async loadSelectedROIsShareView(roisResponse) {
        if (roisResponse.length) {
            this.tabService.openRegionsTab();
            await this.statusMessageService.updateStatus("Start loading ROIs", false, true, 500);
            for (let i = 0; i < roisResponse.length; i++) {
                // TODO: check if we need to add the dbID below
                this.createNewROI(null, null, false, false, roisResponse[i]);
            }
        }

    }

    createNewRectangularROI(startPos, dimensions, color, autoGenerated) {
        let newROI = new RectangularROI(startPos, color, dimensions, autoGenerated);

        this.rectangularRois.push(newROI);

        this.activeRectangularROI = newROI;
        this.addRectangularROITo2DScene(newROI);

    };

    updateROISelection(roi: any, isSelected: boolean, isGlobalSelection: boolean = true): void {
        const roiInService = this.rois.find(r => r.uuid === roi.uuid);
        if (roiInService) {
            roiInService.selected = isSelected;
        }

        if (isGlobalSelection) {
            // Also update the ROI in the userData (used by the ellipsoids left side view)
            if (this.userService && this.userService.user && this.userService.user.saved_data) {
                const userRoi = this.userService.user.saved_data.rois.find(r => r.id === roi.id);
                if (userRoi) {
                    userRoi.selected = isSelected;
                }
                // Emit the updated user data so any subscribers (e.g. the ellipsoids component) refresh
                this.userService.subjectUserData.next(this.userService.user);
            }
        }

        // Update 3D and 2D scenes based on selection
        if (roiInService) {
            if (isSelected) {
                this.addROITo3DScene(roiInService, roiInService.mesh.position, roiInService.mesh.scale);
                this.addROITo2DScene(roiInService);
            } else {
                this.removeROIFrom3DScene(roiInService);
                this.removeROIFrom2DScene(roiInService);
            }
        }
    }



}
